type QuadPoint = {
    x: number;
    y: number;
};

type TemplateDimensions = {
    widthMeters?: number | null;
    heightMeters?: number | null;
} | null | undefined;

type ProjectablePatternPoint = {
    gpsLat?: unknown;
    gpsLon?: unknown;
    xLocal?: unknown;
    yLocal?: unknown;
};

const UNIT_SQUARE: QuadPoint[] = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
];

const toFiniteNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) {
        return null;
    }

    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const extractAreaQuad = (geom: any): QuadPoint[] | null => {
    if (!geom) {
        return null;
    }

    let coords: any = null;
    if (geom.type === 'Polygon') {
        coords = geom.coordinates?.[0];
    } else if (geom.type === 'MultiPolygon') {
        coords = geom.coordinates?.[0]?.[0];
    }

    if (!Array.isArray(coords) || coords.length < 4) {
        return null;
    }

    const first = coords[0];
    const last = coords[coords.length - 1];
    const isClosed = Array.isArray(first)
        && Array.isArray(last)
        && first[0] === last[0]
        && first[1] === last[1];
    const ring = isClosed ? coords.slice(0, -1) : coords;

    if (ring.length !== 4) {
        return null;
    }

    const quad = ring.map((point: any) => ({
        x: toFiniteNumber(point?.[0]) ?? NaN,
        y: toFiniteNumber(point?.[1]) ?? NaN,
    }));

    return quad.some((point) => !Number.isFinite(point.x) || !Number.isFinite(point.y))
        ? null
        : quad;
};

const solveLinearSystem = (matrix: number[][], vector: number[]): number[] | null => {
    const dimension = vector.length;

    for (let column = 0; column < dimension; column += 1) {
        let pivotRow = column;
        let max = Math.abs(matrix[column][column]);

        for (let row = column + 1; row < dimension; row += 1) {
            const value = Math.abs(matrix[row][column]);
            if (value > max) {
                max = value;
                pivotRow = row;
            }
        }

        if (max === 0 || !Number.isFinite(max)) {
            return null;
        }

        if (pivotRow !== column) {
            [matrix[pivotRow], matrix[column]] = [matrix[column], matrix[pivotRow]];
            [vector[pivotRow], vector[column]] = [vector[column], vector[pivotRow]];
        }

        const pivot = matrix[column][column];
        for (let row = column + 1; row < dimension; row += 1) {
            const factor = matrix[row][column] / pivot;
            if (!Number.isFinite(factor)) {
                continue;
            }

            for (let index = column; index < dimension; index += 1) {
                matrix[row][index] -= factor * matrix[column][index];
            }
            vector[row] -= factor * vector[column];
        }
    }

    const result = new Array<number>(dimension);
    for (let row = dimension - 1; row >= 0; row -= 1) {
        let sum = vector[row];
        for (let column = row + 1; column < dimension; column += 1) {
            sum -= matrix[row][column] * result[column];
        }

        const denominator = matrix[row][row];
        if (denominator === 0 || !Number.isFinite(denominator)) {
            return null;
        }

        result[row] = sum / denominator;
    }

    return result;
};

const computeHomography = (source: QuadPoint[], destination: QuadPoint[]): number[] | null => {
    if (source.length !== 4 || destination.length !== 4) {
        return null;
    }

    const matrix: number[][] = [];
    const vector: number[] = [];

    for (let index = 0; index < 4; index += 1) {
        const { x, y } = source[index];
        const { x: u, y: v } = destination[index];

        matrix.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
        vector.push(u);
        matrix.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
        vector.push(v);
    }

    return solveLinearSystem(matrix, vector);
};

const applyHomography = (matrix: number[], x: number, y: number): QuadPoint | null => {
    if (!matrix || matrix.length !== 8) {
        return null;
    }

    const [h11, h12, h13, h21, h22, h23, h31, h32] = matrix;
    const denominator = h31 * x + h32 * y + 1;
    if (denominator === 0 || !Number.isFinite(denominator)) {
        return null;
    }

    const projectedX = (h11 * x + h12 * y + h13) / denominator;
    const projectedY = (h21 * x + h22 * y + h23) / denominator;
    if (!Number.isFinite(projectedX) || !Number.isFinite(projectedY)) {
        return null;
    }

    return {
        x: projectedX,
        y: projectedY,
    };
};

const normalizeLocalPoint = (
    point: ProjectablePatternPoint,
    options: {
        boxWidth?: unknown;
        boxHeight?: unknown;
        templateDimensions?: TemplateDimensions;
    } = {}
): QuadPoint | null => {
    const xLocal = toFiniteNumber(point.xLocal);
    const yLocal = toFiniteNumber(point.yLocal);

    if (xLocal === null || yLocal === null) {
        return null;
    }

    if (xLocal >= 0 && xLocal <= 1 && yLocal >= 0 && yLocal <= 1) {
        return { x: xLocal, y: yLocal };
    }

    const boxWidth = toFiniteNumber(options.boxWidth)
        ?? toFiniteNumber(options.templateDimensions?.widthMeters);
    const boxHeight = toFiniteNumber(options.boxHeight)
        ?? toFiniteNumber(options.templateDimensions?.heightMeters);

    if (!boxWidth || !boxHeight || boxWidth <= 0 || boxHeight <= 0) {
        return null;
    }

    return {
        x: xLocal / boxWidth,
        y: yLocal / boxHeight,
    };
};

export const projectPatternPointToGps = (
    point: ProjectablePatternPoint,
    areaGeometry: any,
    options: {
        boxWidth?: unknown;
        boxHeight?: unknown;
        templateDimensions?: TemplateDimensions;
    } = {}
): { lat: number; lng: number } | null => {
    const gpsLat = toFiniteNumber(point.gpsLat);
    const gpsLon = toFiniteNumber(point.gpsLon);
    if (gpsLat !== null && gpsLon !== null) {
        return {
            lat: gpsLat,
            lng: gpsLon,
        };
    }

    const quad = extractAreaQuad(areaGeometry);
    if (!quad) {
        return null;
    }

    const normalizedPoint = normalizeLocalPoint(point, options);
    if (!normalizedPoint) {
        return null;
    }

    const matrix = computeHomography(UNIT_SQUARE, quad);
    if (!matrix) {
        return null;
    }

    const projected = applyHomography(matrix, normalizedPoint.x, normalizedPoint.y);
    if (!projected) {
        return null;
    }

    return {
        lat: projected.y,
        lng: projected.x,
    };
};
